import { useState, useContext } from 'react';
import { Pencil, Trash2, Plus } from 'lucide-react';
import { format } from 'date-fns';
import { toast } from 'sonner';

import { useStoreCurrencyQuery } from '@/graphql/queries/storeCurrency.generated';
import { useStoreOnboardedDateQuery } from '@/graphql/queries/storeOnboardedDate.generated';
import { useRemoveStoreAssetMutation } from '@/graphql/mutations/removeStoreAsset.generated';
import { useCreateStoreAssetMutation } from '@/graphql/mutations/createStoreAsset.generated';
import { useUpdateStoreAssetMutation } from '@/graphql/mutations/updateStoreAsset.generated';

import { useAllAssetTypes } from './useAllAssetTypes';
import { useStoreAssets, StoreAsset } from './useStoreAssets';
import {
  billingEndToLastBilledDay,
  formatBillingFrequency,
  getBackendErrorDetail,
  lastBilledDayToBillingEnd,
} from './utils';

import {
  CanAccessAssetInternalComment,
  ViewerContext,
} from '@/providers/ViewerContextProvider';

import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Card } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';

import { AssetFormDialog, AssetFormState } from './components/AssetFormDialog';
import { AssetPagination } from './components/AssetPagination';
import {
  getImageUrl,
  DEFAULT_TRACKABLE_ITEM_TYPE_ICON,
} from '@/utils/imageUrl';
import { DEFAULT_CURRENCY } from '@/utils/price';
import { formatPrice } from '@/utils/formatter';
import i18n from '@/utils/i18n';
import { cn } from '@/lib/utils';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';

/**
 * An asset whose billing_end is still in the future cannot be removed: removal sets the
 * DepartureDate to now, and the invoice generator clips billing at min(billing_end,
 * DepartureDate), so removing early would silently cut the committed billing window short.
 * The backend (StoreAsset.remove) enforces this too.
 */
function billingEndsInFuture(billingEnd?: string | null): boolean {
  return !!billingEnd && new Date(billingEnd).getTime() > Date.now();
}

export function MerchantEditAssets({ storeId }: { storeId: string }) {
  const [selectedAsset, setSelectedAsset] = useState<StoreAsset | null>(null);
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [assetToDelete, setAssetToDelete] = useState<StoreAsset | null>(null);
  const [markDeleted, setMarkDeleted] = useState(false);

  const { data: currencyData } = useStoreCurrencyQuery({
    variables: { id: storeId },
    skip: !storeId,
  });
  const currencySymbol = currencyData?.store?.currency_symbol;

  const { data: onboardedDateData } = useStoreOnboardedDateQuery({
    variables: { id: storeId },
    skip: !storeId,
  });
  const onboardedDate = onboardedDateData?.store?.onboarded_date
    ? new Date(onboardedDateData.store.onboarded_date)
    : null;

  const [showRemovedAssets, setShowRemovedAssets] = useState(false);

  // Store's current assets (paginated table)
  const [currentPage, setCurrentPage] = useState(1);
  const {
    items: storeAssets,
    totalPages,
    initialLoading,
    error,
    refetch,
  } = useStoreAssets({
    storeId,
    page: currentPage,
    hideDeleted: !showRemovedAssets,
  });

  // All asset types for the add dialog dropdown
  const { items: assetTypes, loading: assetTypesLoading } = useAllAssetTypes({
    storeId,
    page: 1,
    pageSize: 10000, // Fetch all asset types for dropdown
  });

  // The dialog hides the internal comment from roles colugo does not let read
  // it, so their form state carries an empty string that must not travel as a
  // `null` the backend could read as "clear it".
  const viewer = useContext(ViewerContext);
  const canAccessInternalComment = CanAccessAssetInternalComment(viewer);

  const [removeStoreAsset, { loading: removeLoading }] =
    useRemoveStoreAssetMutation();
  const [createStoreAsset] = useCreateStoreAssetMutation();
  const [updateStoreAsset] = useUpdateStoreAssetMutation();

  const handleCreateAsset = async (data: AssetFormState) => {
    if (!data.itemTypeId) return;

    try {
      await createStoreAsset({
        variables: {
          input: {
            base: {
              store_id: storeId,
              item_type_id: data.itemTypeId,
              description: data.description || null,
              ...(canAccessInternalComment && {
                internal_comment: data.internalComment || null,
              }),
              price: data.price,
              billing_frequency: data.billingFrequency,
              billing_start_with_onboarding:
                data.billingStartOption === 'onboarding',
              billing_start:
                data.billingStartOption === 'custom' && data.billingStart
                  ? // Send the picked calendar day, not an instant. toISOString()
                    // applies the local→UTC offset, which shifts midnight in a
                    // positive-offset zone (e.g. Berlin) back to the previous
                    // day/month — the backend then rejects it as a month before
                    // the asset's arrival. Matches handleUpdateAsset.
                    format(data.billingStart, 'yyyy-MM-dd')
                  : null,
              // The picker holds the inclusive last billed day; the backend wants
              // the exclusive end (first un-billed day = last day + 1).
              billing_end:
                data.billingEndOption === 'custom' && data.billingEnd
                  ? lastBilledDayToBillingEnd(data.billingEnd)
                  : null,
              custom_fields:
                data.customFields.length > 0 ? data.customFields : undefined,
            },
          },
        },
      });
      refetch();
    } catch (err) {
      console.error('Failed to create asset', err);
      // Re-throw so AssetFormDialog's catch shows the error toast instead of a
      // false success. Without this, any backend rejection (e.g. an invalid
      // billing_start) looks like a successful add.
      throw err;
    }
  };

  const handleUpdateAsset = async (assetId: string, data: AssetFormState) => {
    await updateStoreAsset({
      variables: {
        id: assetId,
        input: {
          base: {
            description: data.description || null,
            ...(canAccessInternalComment && {
              internal_comment: data.internalComment || null,
            }),
            price: data.price,
            billing_frequency: data.billingFrequency,
            billing_start_with_onboarding:
              data.billingStartOption === 'onboarding',
            billing_start:
              data.billingStartOption === 'custom' && data.billingStart
                ? format(data.billingStart, 'yyyy-MM-dd')
                : null,
            // The picker holds the inclusive last billed day; the backend wants
            // the exclusive end (first un-billed day = last day + 1).
            // Note: colugo's updateStoreAsset ignores a null billing_end
            // (`if (input.base.billing_end != null)`), so an end date can be set
            // or changed but not cleared via this mutation.
            billing_end:
              data.billingEndOption === 'custom' && data.billingEnd
                ? lastBilledDayToBillingEnd(data.billingEnd)
                : null,
            custom_fields: data.customFields,
          },
        },
      },
    });
    setSelectedAsset(null);
    refetch();
  };

  const handleRemoveAsset = async (markDeleted?: boolean) => {
    if (!assetToDelete) return;
    try {
      await removeStoreAsset({
        variables: {
          id: assetToDelete.id,
          ...(markDeleted ? { markDeleted: true } : {}),
        },
      });
      setAssetToDelete(null);
      refetch();
    } catch (err) {
      console.error('Failed to remove asset', err);
      const detail = getBackendErrorDetail(err);
      toast.error(
        i18n.t('Assets:errorRemovingAsset'),
        detail ? { description: detail } : undefined,
      );
    }
  };

  return (
    <div className="tw:flex tw:flex-col tw:gap-6 tw:w-full tw:max-w-[872px]">
      {/* Alert Banner */}
      <Alert>
        <AlertDescription className="tw:text-sm tw:font-medium tw:text-foreground">
          <span className="tw:font-bold">
            {i18n.t('Invoices:pleaseNote')}:{' '}
          </span>
          Assets will only appear on the partner invoice if the first date of
          billing is within or before the billing period. If the first date of
          billing is within the billing month, the cost will be calculated
          proportionally to the days left in the month after the first date of
          billing. The partner is not charged for the day of the onboarding.
        </AlertDescription>
      </Alert>

      {/* Table Section */}
      <div className="tw:flex tw:flex-col tw:gap-4">
        {/* Add Asset Button */}
        <div className="tw:flex tw:justify-between">
          <div className="tw:flex tw:items-center tw:gap-2">
            <Switch
              id="show-removed-assets"
              checked={showRemovedAssets}
              onCheckedChange={setShowRemovedAssets}
            />
            <Label htmlFor="show-removed-assets">
              {i18n.t('Assets:showRemovedAssets')}
            </Label>
          </div>
          <Button variant="outline" onClick={() => setIsAddDialogOpen(true)}>
            <Plus className="tw:size-4" />
            {i18n.t('Assets:addAsset')}
          </Button>
        </div>

        {/* Assets Table */}
        <Card className="tw:p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="tw:w-[88px] tw:pl-4 tw:h-13">
                  {i18n.t('Common:image')}
                </TableHead>
                <TableHead className="tw:w-[240px]">
                  {i18n.t('Common:name')}
                </TableHead>
                <TableHead>{i18n.t('ItemType:price')}</TableHead>
                <TableHead>{i18n.t('Assets:startingOn')}</TableHead>
                <TableHead className="tw:w-[100px] tw:text-right" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {initialLoading && (
                <>
                  {Array.from({ length: 5 }).map((_, index) => (
                    <TableRow key={index}>
                      <TableCell>
                        <Skeleton className="tw:size-14 tw:rounded" />
                      </TableCell>
                      <TableCell>
                        <Skeleton className="tw:h-4 tw:w-32" />
                      </TableCell>
                      <TableCell>
                        <Skeleton className="tw:h-4 tw:w-16" />
                      </TableCell>
                      <TableCell>
                        <Skeleton className="tw:h-4 tw:w-20" />
                      </TableCell>
                      <TableCell>
                        <Skeleton className="tw:h-8 tw:w-16" />
                      </TableCell>
                    </TableRow>
                  ))}
                </>
              )}

              {error && (
                <TableRow>
                  <TableCell
                    colSpan={5}
                    className="tw:text-center tw:text-muted-foreground"
                  >
                    {i18n.t('Assets:errorLoadingAssets')}
                    {error?.message && <> ({error.message})</>}
                  </TableCell>
                </TableRow>
              )}

              {!initialLoading && !error && storeAssets.length === 0 && (
                <TableRow>
                  <TableCell
                    colSpan={5}
                    className="tw:text-center tw:text-muted-foreground"
                  >
                    {i18n.t('Assets:noAssetsFound')}
                  </TableCell>
                </TableRow>
              )}

              {storeAssets.map((asset) => {
                const imageSrc = getImageUrl(
                  56,
                  asset.item_type.image_url || DEFAULT_TRACKABLE_ITEM_TYPE_ICON,
                );

                const removedAt = asset.removed_at;
                // Block condition uses the raw (exclusive) end — removal only
                // becomes possible on that first un-billed day. The displayed
                // date is the inclusive last billed day, matching the rest of
                // the UI.
                const removeBlockedUntil =
                  !removedAt &&
                  asset.billing_end &&
                  billingEndsInFuture(asset.billing_end)
                    ? format(
                        billingEndToLastBilledDay(asset.billing_end),
                        'dd.MM.yyyy',
                      )
                    : null;

                return (
                  <TableRow
                    key={asset.id}
                    className={cn(
                      'tw:*:first:pl-4 tw:*:last:pr-4 tw:*:last:py-4',
                      !!removedAt && 'tw:opacity-50',
                    )}
                  >
                    {/* Image */}
                    <TableCell>
                      <img
                        src={imageSrc}
                        alt={asset.item_type.name}
                        className="tw:size-14 tw:object-contain"
                      />
                    </TableCell>

                    {/* Name + ID */}
                    <TableCell>
                      <div className="tw:flex tw:flex-col">
                        <span className="tw:text-sm">
                          {asset.item_type.name}
                        </span>
                        <span className="tw:text-xs tw:text-muted-foreground">
                          #{asset.id}
                        </span>
                        {removedAt && (
                          <span className="tw:text-xs tw:text-muted-foreground">
                            {i18n.t('Common:removedAt')}{' '}
                            {format(removedAt, 'dd.MM.yyyy')}
                          </span>
                        )}
                      </div>
                    </TableCell>

                    {/* Price + Billing Frequency */}
                    <TableCell>
                      <div className="tw:flex tw:flex-col">
                        <span className="tw:text-sm">
                          {formatPrice(
                            asset.price ?? 0,
                            null,
                            false,
                            false,
                            currencySymbol ?? DEFAULT_CURRENCY,
                          )}
                        </span>
                        <span className="tw:text-xs tw:text-muted-foreground">
                          {formatBillingFrequency(asset.billing_frequency)}
                        </span>
                      </div>
                    </TableCell>

                    {/* Starting on */}
                    <TableCell>
                      <div className="tw:flex tw:flex-col">
                        <span className="tw:text-sm tw:text-foreground">
                          {asset.billing_start_with_onboarding
                            ? i18n.t('Assets:withOnboarding')
                            : asset.billing_start
                              ? format(asset.billing_start, 'dd.MM.yyyy')
                              : '-'}
                        </span>
                        {asset.billing_end && (
                          <span className="tw:text-xs tw:text-muted-foreground">
                            {i18n.t('Assets:untilDate', {
                              date: format(
                                billingEndToLastBilledDay(asset.billing_end),
                                'dd.MM.yyyy',
                              ),
                            })}
                          </span>
                        )}
                      </div>
                    </TableCell>

                    {/* Actions */}
                    <TableCell>
                      <div className="tw:flex tw:items-center tw:justify-end tw:gap-1">
                        <Button
                          variant="outline"
                          size="icon-sm"
                          onClick={() => setSelectedAsset(asset)}
                          disabled={!!removedAt}
                        >
                          <Pencil className="tw:size-4" />
                        </Button>
                        {removeBlockedUntil ? (
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span tabIndex={0} className="tw:inline-flex">
                                <Button
                                  variant="outline"
                                  size="icon-sm"
                                  disabled
                                >
                                  <Trash2 className="tw:size-4" />
                                </Button>
                              </span>
                            </TooltipTrigger>
                            <TooltipContent>
                              {i18n.t('Assets:cannotRemoveBeforeBillingEnd', {
                                date: removeBlockedUntil,
                              })}
                            </TooltipContent>
                          </Tooltip>
                        ) : (
                          <Button
                            variant="outline"
                            size="icon-sm"
                            onClick={() => setAssetToDelete(asset)}
                            disabled={!!removedAt}
                          >
                            <Trash2 className="tw:size-4" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Card>

        {/* Pagination */}
        <AssetPagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={setCurrentPage}
        />
      </div>

      {/* Edit Asset Dialog */}
      <AssetFormDialog
        mode="edit"
        asset={selectedAsset}
        currencySymbol={currencySymbol || DEFAULT_CURRENCY}
        isOpen={!!selectedAsset}
        onOpenChange={(open) => !open && setSelectedAsset(null)}
        onEdit={handleUpdateAsset}
        loading={assetTypesLoading}
        onboardedDate={onboardedDate}
      />

      {/* Add Asset Dialog */}
      <AssetFormDialog
        mode="add"
        isOpen={isAddDialogOpen}
        onOpenChange={setIsAddDialogOpen}
        itemTypes={assetTypes}
        currencySymbol={currencySymbol || DEFAULT_CURRENCY}
        onAdd={handleCreateAsset}
        loading={assetTypesLoading}
        onboardedDate={onboardedDate}
      />

      {/* Remove Asset Confirmation Dialog */}
      <AlertDialog
        open={!!assetToDelete}
        onOpenChange={(open) => {
          if (!open) {
            setAssetToDelete(null);
            setMarkDeleted(false);
          }
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {i18n.t('Assets:removeAssetTitle')}
            </AlertDialogTitle>
            <AlertDialogDescription className="tw:flex tw:flex-col tw:gap-3">
              <span>{i18n.t('Assets:removeAssetDescription')}</span>
              <span>{i18n.t('Assets:removeAssetPastBillingHint')}</span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <label className="tw:flex tw:items-center tw:gap-2 tw:cursor-pointer">
            <Checkbox
              checked={markDeleted}
              onCheckedChange={(checked) => setMarkDeleted(checked === true)}
            />
            <span className="tw:text-sm">
              {i18n.t('Assets:removeAssetFromPastBilling')}
            </span>
          </label>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={removeLoading}>
              {i18n.t('Common:cancel')}
            </AlertDialogCancel>
            <AlertDialogAction
              variant="destructive"
              onClick={() => handleRemoveAsset(markDeleted || undefined)}
              disabled={removeLoading}
            >
              {i18n.t('Assets:removeAsset')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
